import { takeLatest, call, put, all, select } from 'redux-saga/effects'
import { delay } from 'redux-saga'
import { normalize } from 'normalizr'
import { push as pushHistory } from 'react-router-redux'

import actions, { constants } from 'common/actions/auth'
import snackbarActions from 'common/actions/snackbar'

import * as schemas from 'schemas'
import * as api from 'api/auth'
import * as userApi from 'api/user'

import * as Strings from 'constants/Strings'
import * as Routes from 'constants/Routes'
import { clearAuthStoreData } from 'utils/authStore'
import { clearDashboardWorkspace } from 'modules/Dashboard/dashboardWorkspaceStorage'
import { resetDashboardWorkspace } from 'modules/Dashboard/actions'

const SESSION_RETRY_DELAY = 1000

function* postAuth(routing) {
  if (routing.locationBeforeTransitions.pathname === Routes.MAIN) {
    return yield put(pushHistory(Routes.DASHBOARD))
  }
  yield put(pushHistory(routing.locationBeforeTransitions))
}

// v6 fetchAuthSession returns { tokens: { accessToken, idToken } }.
// accessToken.toString() is the raw JWT; accessToken.payload holds the claims.
const getUsernameFromTokens = tokens => tokens?.accessToken?.payload?.username
export function* onAuthUser() {
  yield put(actions.authUserRequest.start())

  const routing = yield select(state => state.routing)

  // Pages to ignore
  if (routing.locationBeforeTransitions.pathname.includes(Routes.LOGIN)) {
    return yield put(actions.authUserRequest.failure(null))
  }

  let session = yield call(api.getCognitoSession)

  // A transient failure while refreshing is the difference between "cannot check"
  // and "logged out" for the user, so give it one more chance before deciding.
  if (session.status === api.SESSION_UNKNOWN) {
    yield call(delay, SESSION_RETRY_DELAY)
    session = yield call(api.getCognitoSession)
  }

  if (session.status === api.SESSION_UNKNOWN) {
    // Amplify kept the tokens, so the session is probably still fine and a reload
    // may well succeed. Say the check failed rather than implying it ended.
    yield put(actions.authUserRequest.failure(session.error))
    yield put(pushHistory(Routes.LOGIN))
    return yield put(snackbarActions.showSnackbar(Strings.SESSION_CHECK_FAILED))
  }

  if (session.status === api.SESSION_ENDED) {
    // Cognito rejected the refresh token: revoked, reused, or the account can no
    // longer sign in. This is the one case where the session really did end.
    yield put(actions.authUserRequest.failure(session.error))
    yield put(pushHistory(Routes.LOGIN))
    return yield put(snackbarActions.showSnackbar(Strings.EXPIRED_TOKEN))
  }

  if (session.status !== api.SESSION_ACTIVE) {
    // No tokens at all: never signed in, signed out cleanly, or the refresh token
    // expired. Indistinguishable here, so route to login without a message. A
    // first-time visitor must not be told their session expired.
    yield put(actions.authUserRequest.failure(null))
    return yield put(pushHistory(Routes.LOGIN))
  }

  try {
    yield call(clearAuthStoreData)
    const { user } = yield call(userApi.getCognitoUser, {
      username: getUsernameFromTokens(session.tokens)
    }) // Pedimos el usuario de la Dynamo
    const norm = yield call(normalize, user, schemas.user)
    yield put(actions.authUserRequest.success(norm))
    yield call(postAuth, routing)
  } catch (err) {
    // Cognito handed us tokens a moment ago, so reaching here means the profile
    // request failed — a backend, gateway or network problem, not an expired
    // session. Say that instead of blaming the token.
    yield put(actions.authUserRequest.failure(err))
    yield put(pushHistory(Routes.LOGIN))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
  }
}

export function* onLoginCognito({ payload: data }) {
  yield put(actions.loginRequest.start())

  try {
    // v6 signIn returns { isSignedIn, nextStep }; on bad credentials the api
    // layer returns the thrown error, which has no isSignedIn flag.
    const result = yield call(api.loginCognito, data)
    if (!result || !result.isSignedIn) {
      yield put(actions.loginRequest.failure(result))
    } else {
      const session = yield call(api.getCognitoSession)
      if (session.status !== api.SESSION_ACTIVE) {
        throw new Error('Missing session after sign in')
      }

      yield call(clearAuthStoreData)

      const { user } = yield call(userApi.getCognitoUser, {
        username: getUsernameFromTokens(session.tokens)
      }) // Pedimos el usuario de la Dynamo
      const norm = yield call(normalize, user, schemas.user)
      yield put(actions.loginRequest.success(norm))
      yield put(pushHistory(Routes.DASHBOARD))
    }
  } catch (err) {
    yield put(actions.loginRequest.failure(err))
  }
}

export function* onLogoutCognito() {
  yield call(api.logoutCognito)
  yield call(clearAuthStoreData)
  yield call(clearDashboardWorkspace)
  yield put(resetDashboardWorkspace())
  yield put(pushHistory(Routes.LOGIN))
}

export default function* watchAuth() {
  yield all([
    takeLatest(constants.AUTH_USER, onAuthUser),
    takeLatest(constants.LOGIN, onLoginCognito),
    takeLatest(constants.LOGOUT, onLogoutCognito)
  ])
}
